import type { ComponentType } from 'react'
import { Navigate, Outlet, createBrowserRouter, type RouteObject } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import RootRedirect from '@/pages/root'
import AthleteHome from '@/pages/root/AthleteHome'
import RouteErrorPage from '@/pages/route-error'

const RELOADED_KEY = 'td-chunk-reload'

/**
 * After a deploy, a tab opened on the old build asks for page chunks that no longer exist. Reload
 * once to pick up the new build instead of showing an error; a second failure in the same session
 * falls through to the error page so a real outage never loops.
 */
export async function importOrReload<T>(importer: () => Promise<T>): Promise<T> {
  try {
    const mod = await importer()
    try {
      sessionStorage.removeItem(RELOADED_KEY)
    } catch {
      // Storage blocked: nothing to clear.
    }
    return mod
  } catch (error) {
    let reloaded = true
    try {
      reloaded = sessionStorage.getItem(RELOADED_KEY) === '1'
      if (!reloaded) sessionStorage.setItem(RELOADED_KEY, '1')
    } catch {
      // Storage blocked: never reload, or a stale tab could loop.
    }
    if (!reloaded) {
      window.location.reload()
      return new Promise<T>(() => {})
    }
    throw error
  }
}

function lazyPage(importer: () => Promise<{ default: ComponentType }>) {
  return async () => ({ Component: (await importOrReload(importer)).default })
}

/** The first load waits on a lazy page; render nothing meanwhile (the HTML shell shows the brand). */
function HydrateFallback() {
  return null
}

function AppShellLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}

function AppShellErrorLayout() {
  return (
    <AppShell>
      <RouteErrorPage />
    </AppShell>
  )
}

const shellRoutes: RouteObject[] = [
  { path: '/a/:slug', Component: AthleteHome },
  { path: '/a/:slug/today', lazy: lazyPage(() => import('@/pages/today')) },
  { path: '/a/:slug/fitness', lazy: lazyPage(() => import('@/pages/fitness')) },
  { path: '/a/:slug/wellness', lazy: lazyPage(() => import('@/pages/wellness')) },
  {
    path: '/a/:slug/wellness/:metric',
    lazy: lazyPage(() => import('@/pages/wellness-metric')),
  },
  { path: '/a/:slug/activities', lazy: lazyPage(() => import('@/pages/activities')) },
  { path: '/a/:slug/activities/:id', lazy: lazyPage(() => import('@/pages/activity-detail')) },
  { path: '/a/:slug/calendar', lazy: lazyPage(() => import('@/pages/calendar')) },
  { path: '/a/:slug/routes', lazy: lazyPage(() => import('@/pages/routes')) },
  { path: '/a/:slug/routes/:routeId', lazy: lazyPage(() => import('@/pages/route-detail')) },
  { path: '/a/:slug/events/:id', lazy: lazyPage(() => import('@/pages/event-detail')) },
  { path: '/a/:slug/review/:period/:date', lazy: lazyPage(() => import('@/pages/review')) },
  { path: '/a/:slug/plan', lazy: lazyPage(() => import('@/pages/plan')) },
  { path: '/a/:slug/records', lazy: lazyPage(() => import('@/pages/records')) },
  { path: '/a/:slug/strength', lazy: lazyPage(() => import('@/pages/strength')) },
  // Reports never shipped; the reviews live under Calendar. Old links land there.
  { path: '/a/:slug/reports', element: <Navigate to="../calendar" relative="path" replace /> },
  { path: '/a/:slug/settings', lazy: lazyPage(() => import('@/pages/settings')) },
  { path: '/compare', lazy: lazyPage(() => import('@/pages/compare')) },
  { path: '/sync', lazy: lazyPage(() => import('@/pages/sync')) },
  { path: '*', lazy: lazyPage(() => import('@/pages/not-found')) },
]

const routes: RouteObject[] = [
  { path: '/', Component: RootRedirect },
  // Sign-in pages sit outside the shell: no nav, no data until the viewer is in.
  { path: '/login', lazy: lazyPage(() => import('@/pages/login')), HydrateFallback },
  { path: '/welcome', lazy: lazyPage(() => import('@/pages/welcome')), HydrateFallback },
  { path: '/onboarding', lazy: lazyPage(() => import('@/pages/onboarding')), HydrateFallback },
  {
    element: <AppShellLayout />,
    errorElement: <AppShellErrorLayout />,
    HydrateFallback,
    children: shellRoutes,
  },
]

if (import.meta.env.DEV) {
  routes.push({ path: '/_kit', lazy: lazyPage(() => import('@/pages/kit')) })
}

export const router = createBrowserRouter(routes)
